// FILE: useModelSelectionPolicy.ts
// Purpose: Read the current user's model/agent policy in the shape
//          `applyModelSelectionPolicy` expects.
// Layer: Web hooks
// Exports: useModelSelectionPolicy.
//
// Every path that decides what model a thread starts with needs the same three
// facts — allowed models, visible agents, default agent — so they live behind
// one hook rather than being re-assembled per call site.

import { useMemo } from "react";

import type { ModelSelectionPolicy } from "~/allowedModelSelection";
import { useAuthStore } from "~/auth/authStore";
import { useVisibleManagedAgents } from "~/hooks/useProviderModelCatalog";

/**
 * The policy to reconcile a preferred selection against, or `null` while it is
 * unknown.
 *
 * `null` is returned until the session is authenticated, which covers both the
 * in-flight `/auth/me` and the auth-disabled deployment. In those states the
 * store holds an empty allowlist that means "not loaded", not "denied", and
 * reconciling against it would replace a perfectly good model with an agent.
 */
export function useModelSelectionPolicy(): ModelSelectionPolicy | null {
  const status = useAuthStore((state) => state.status);
  const allowedModelKeys = useAuthStore((state) => state.allowedModelKeys);
  const defaultAgentId = useAuthStore((state) => state.defaultAgentId);
  const agents = useVisibleManagedAgents();

  return useMemo(
    () => (status === "authenticated" ? { allowedModelKeys, agents, defaultAgentId } : null),
    [status, allowedModelKeys, agents, defaultAgentId],
  );
}
